/**
 * People (Stepping Stones) tab. Two view modes:
 *   - "grid"  → existing card grid (default)
 *   - "path"  → new Career Path SVG visualization
 *
 * Modes share the same data source and refresh logic so AI deep-dives + the
 * "Touched today" stamp propagate to both views without a full reload.
 */
import React, { useEffect, useRef, useState } from "react";
import ResmoMascot from "../ResmoMascot";
import ContactCard from "./ContactCard";
import CareerPath from "./CareerPath";
import ResultSection from "../ResultSection";
import { api, friendlyAxiosError } from "../../lib/api";
import { Plus, Upload, Loader, LayoutGrid, MapPin, HelpCircle, ExternalLink } from "lucide-react";

const CSV_STEPS = [
  "Sign in to LinkedIn on a desktop browser.",
  "Click your photo → Settings & Privacy → Data privacy.",
  "Click \"Get a copy of your data\" → choose \"Want something in particular?\".",
  "Tick only \"Connections\" → Request archive.",
  "LinkedIn emails you a .zip in 10–20 minutes. Inside is Connections.csv.",
  "Drop that file into the Import LinkedIn CSV button. Resmo never sees anything else.",
];

function CsvInstructions({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="rm-card-static p-4 md:p-5 mb-2"
      style={{ background: "#FFFBEB", border: "3px dashed #FFC145" }}
      data-testid="csv-instructions"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="rm-label" style={{ color: "#92400E" }}>· How to export your LinkedIn connections</div>
          <div className="font-display text-base uppercase">Six clicks. No scraping.</div>
        </div>
        <a
          href="https://www.linkedin.com/mypreferences/d/download-my-data"
          target="_blank"
          rel="noopener noreferrer"
          className="rm-btn rm-btn-secondary text-[11px] px-2.5 py-1.5"
          data-testid="csv-help-open-linkedin"
        >
          Open LinkedIn <ExternalLink size={11} />
        </a>
      </div>
      <ol className="text-sm space-y-1.5 list-none">
        {CSV_STEPS.map((s, i) => (
          <li key={i} className="flex gap-2">
            <span className="font-display text-xs flex-shrink-0" style={{ color: "#92400E" }}>{String(i + 1).padStart(2, "0")}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      <button onClick={onClose} className="rm-btn rm-btn-ghost text-[10px] px-2 py-1 mt-3" data-testid="csv-help-close">
        Got it
      </button>
    </div>
  );
}

export default function PeoplePanel({ onJumpTo }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [view, setView] = useState("grid"); // 'grid' | 'path'
  const [showHelp, setShowHelp] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({ name: "", role: "", company: "", linkedin_url: "", interests: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/network/contacts");
      setContacts(data.contacts || []);
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const reset = () => setForm({ name: "", role: "", company: "", linkedin_url: "", interests: "", notes: "" });

  const submit = async () => {
    setError("");
    if (form.name.trim().length < 2) {
      setError("A name is the minimum Resmo needs.");
      return;
    }
    try {
      await api.post("/network/contacts", form);
      reset();
      setAdding(false);
      load();
    } catch (e) {
      setError(friendlyAxiosError(e));
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    upload(f);
    e.target.value = "";
  };
  const upload = async (file) => {
    setImporting(true);
    setImportMsg("");
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/network/contacts/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportMsg(`Imported ${data.inserted} contacts (skipped ${data.skipped}).`);
      load();
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="people-panel" className="space-y-5">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>· Your contact graph</div>
          <h2 className="font-display text-2xl uppercase">Each name is a stepping stone.</h2>
          <p className="text-sm mt-1" style={{ color: "#475569" }}>
            Resmo never scrapes LinkedIn. Add contacts manually, or paste your
            <span className="font-semibold"> own </span>
            LinkedIn Connections.csv export.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* View switch */}
          <div className="inline-flex rounded-md overflow-hidden" style={{ border: "3px solid #0F1B2D" }} data-testid="people-view-switch">
            <button
              onClick={() => setView("grid")}
              className="font-mono-rm text-[10px] uppercase tracking-widest px-2.5 py-1.5 inline-flex items-center gap-1"
              style={{
                background: view === "grid" ? "#0F1B2D" : "white",
                color: view === "grid" ? "#FFC145" : "#0F1B2D",
                fontWeight: 700,
              }}
              data-testid="people-view-grid"
            >
              <LayoutGrid size={11} /> Grid
            </button>
            <button
              onClick={() => setView("path")}
              className="font-mono-rm text-[10px] uppercase tracking-widest px-2.5 py-1.5 inline-flex items-center gap-1"
              style={{
                background: view === "path" ? "#0F1B2D" : "white",
                color: view === "path" ? "#FFC145" : "#0F1B2D",
                fontWeight: 700,
                borderLeft: "3px solid #0F1B2D",
              }}
              data-testid="people-view-path"
            >
              <MapPin size={11} /> Career Path
            </button>
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={importing}
                  className="rm-btn rm-btn-secondary text-xs px-3 py-2"
                  data-testid="import-csv-btn"
                  title="Import a LinkedIn Connections.csv export (Settings → Get a copy of your data → Connections)">
            {importing ? <Loader size={13} className="animate-spin" /> : <Upload size={13} />}
            {importing ? "Importing…" : "Import LinkedIn CSV"}
          </button>
          <button onClick={() => setShowHelp((s) => !s)} className="rm-btn rm-btn-ghost text-xs px-2 py-2"
                  data-testid="csv-help-btn" title="How do I get my LinkedIn CSV?">
            <HelpCircle size={13} /> How?
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onPickFile}
                 className="hidden" data-testid="csv-file-input" />
          <button onClick={() => setAdding(true)} className="rm-btn rm-btn-primary text-xs px-3 py-2"
                  data-testid="add-contact-btn">
            <Plus size={13} /> Add contact
          </button>
        </div>
      </div>

      <CsvInstructions open={showHelp} onClose={() => setShowHelp(false)} />

      {importMsg && (
        <div className="p-3 rounded-md font-mono-rm text-sm"
             style={{ background: "#ECFDF5", border: "3px solid #10B981", color: "#065F46" }}
             data-testid="import-result">
          {importMsg}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-md font-mono-rm text-sm"
             style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
             data-testid="people-error">
          {error}
        </div>
      )}

      {adding && (
        <div className="rm-card-static p-5 md:p-6" data-testid="contact-form">
          <div className="rm-label mb-3" style={{ color: "#FF6A00" }}>· New stepping stone</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="rm-input" placeholder="Name *" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="contact-name" />
            <input className="rm-input" placeholder="Role" value={form.role}
                   onChange={(e) => setForm({ ...form, role: e.target.value })}
                   data-testid="contact-role" />
            <input className="rm-input" placeholder="Company" value={form.company}
                   onChange={(e) => setForm({ ...form, company: e.target.value })}
                   data-testid="contact-company" />
            <input className="rm-input" placeholder="LinkedIn URL (optional)" value={form.linkedin_url}
                   onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                   data-testid="contact-linkedin" />
            <input className="rm-input md:col-span-2" placeholder="Their interests / focus areas"
                   value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })}
                   data-testid="contact-interests" />
            <textarea className="rm-input md:col-span-2" rows={2} placeholder="Notes — how you met, what to remember"
                      value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="contact-notes" />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={submit} className="rm-btn rm-btn-primary text-xs px-3 py-2"
                    data-testid="contact-save">Save stepping stone</button>
            <button onClick={() => { reset(); setAdding(false); }} className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                    data-testid="contact-cancel">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 font-mono-rm">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 rm-card-static" data-testid="people-empty">
          <ResmoMascot state="idle" size={140} />
          <div className="font-display text-2xl uppercase mt-3">No stones yet.</div>
          <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "#475569" }}>
            Start by adding 3 people you've actually spoken to recently — Resmo will
            score how each one fits your career path and suggest the next move.
          </p>
        </div>
      ) : view === "path" ? (
        <CareerPath contacts={contacts} onChanged={load} />
      ) : (
        <ResultSection
          title="Contacts"
          eyebrow="Each name is a stepping stone"
          accentGradient="linear-gradient(135deg,#FF6A00 0%,#B45309 100%)"
          count={contacts.length}
          defaultOpen
          testid="contacts-section"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="people-grid">
            {contacts.map((c) => (
              <ContactCard key={c.id} contact={c} onChanged={load} onJumpTo={onJumpTo} />
            ))}
          </div>
        </ResultSection>
      )}
    </div>
  );
}
